/**
 * @file Navigation grid: walkability, line of sight and A* pathfinding.
 *
 * The dungeon already publishes its solid geometry as a list of merged AABB
 * blockers. Rasterising them once per level into a uniform grid gives the AI
 * three things it cannot get from the collision system, which only ever answers
 * "are these two boxes overlapping *right now*":
 *
 *  - `isClear`      — may a body of radius r stand here?
 *  - `hasLineOfSight` — is the straight segment between two points unobstructed?
 *  - `findPath`     — a route that walks around walls instead of into them.
 *
 * The grid is deliberately free of Three.js and of the ECS: it is plain maths
 * over typed arrays, so it can be unit tested on its own.
 */

/** Largest number of cells a single A* run may expand before giving up. */
const MAX_EXPANSIONS = 6000;
/** Guard against a degenerate ray walking forever. */
const MAX_RAY_STEPS = 4096;

/**
 * @typedef {object} Blocker
 * @property {number} x Centre X.
 * @property {number} z Centre Z.
 * @property {number} halfX Half-extent along X.
 * @property {number} halfZ Half-extent along Z.
 */

/**
 * @typedef {object} NavPoint
 * @property {number} x World X.
 * @property {number} z World Z.
 */

export class NavGrid {
  /**
   * @param {object} options Grid setup.
   * @param {{ minX: number, maxX: number, minZ: number, maxZ: number }} options.bounds World extents.
   * @param {Blocker[]} options.blockers Solid boxes, in world units.
   * @param {number} [options.cellSize] Edge length of one cell.
   * @param {number} [options.solidHeight] Height of the blockers, used by cover queries.
   */
  constructor({ bounds, blockers = [], cellSize = 0.5, solidHeight = 0 }) {
    /** @type {number} */
    this.cellSize = cellSize;
    /** @type {number} Height every blocker is assumed to have. */
    this.solidHeight = solidHeight;

    // Snapping the origin to a multiple of the cell size keeps cell edges
    // aligned with the dungeon's tile edges, so a two-unit corridor rasterises
    // to exactly four half-unit cells instead of leaking into the walls.
    /** @type {number} */
    this.originX = Math.floor(bounds.minX / cellSize) * cellSize;
    /** @type {number} */
    this.originZ = Math.floor(bounds.minZ / cellSize) * cellSize;
    /** @type {number} */
    this.width = Math.max(1, Math.ceil((bounds.maxX - this.originX) / cellSize));
    /** @type {number} */
    this.height = Math.max(1, Math.ceil((bounds.maxZ - this.originZ) / cellSize));

    /** @type {Uint8Array} 1 = solid. */
    this.solid = new Uint8Array(this.width * this.height);
    for (const blocker of blockers) this._rasterize(blocker);

    /** @type {Map<number, Uint8Array>} Eroded grids, keyed by clearance in cells. @private */
    this._eroded = new Map();

    // A* scratch, allocated once. `_stamp` avoids clearing them between runs.
    /** @type {Float32Array} @private */
    this._gScore = new Float32Array(this.solid.length);
    /** @type {Float32Array} @private */
    this._fScore = new Float32Array(this.solid.length);
    /** @type {Int32Array} @private */
    this._cameFrom = new Int32Array(this.solid.length);
    /** @type {Int32Array} @private */
    this._visited = new Int32Array(this.solid.length);
    /** @type {Uint8Array} @private */
    this._closed = new Uint8Array(this.solid.length);
    /** @type {number} @private */
    this._stamp = 0;
    /** @type {number[]} Binary heap of cell indices. @private */
    this._heap = [];
  }

  // ------------------------------------------------------------ cell helpers

  /** @param {Blocker} blocker Solid box. @returns {void} @private */
  _rasterize(blocker) {
    const cs = this.cellSize;
    const x0 = Math.floor((blocker.x - blocker.halfX - this.originX) / cs);
    const x1 = Math.ceil((blocker.x + blocker.halfX - this.originX) / cs) - 1;
    const z0 = Math.floor((blocker.z - blocker.halfZ - this.originZ) / cs);
    const z1 = Math.ceil((blocker.z + blocker.halfZ - this.originZ) / cs) - 1;

    for (let cz = Math.max(0, z0); cz <= Math.min(this.height - 1, z1); cz++) {
      const row = cz * this.width;
      for (let cx = Math.max(0, x0); cx <= Math.min(this.width - 1, x1); cx++) {
        this.solid[row + cx] = 1;
      }
    }
  }

  /** @param {number} x World X. @returns {number} Cell X. */
  cellOfX(x) {
    return Math.floor((x - this.originX) / this.cellSize);
  }

  /** @param {number} z World Z. @returns {number} Cell Z. */
  cellOfZ(z) {
    return Math.floor((z - this.originZ) / this.cellSize);
  }

  /** @param {number} cx Cell X. @returns {number} World X of its centre. */
  centerX(cx) {
    return this.originX + (cx + 0.5) * this.cellSize;
  }

  /** @param {number} cz Cell Z. @returns {number} World Z of its centre. */
  centerZ(cz) {
    return this.originZ + (cz + 0.5) * this.cellSize;
  }

  /**
   * @param {number} cx Cell X.
   * @param {number} cz Cell Z.
   * @returns {boolean} True when the cell is solid or outside the grid.
   */
  isSolidCell(cx, cz) {
    if (cx < 0 || cz < 0 || cx >= this.width || cz >= this.height) return true;
    return this.solid[cz * this.width + cx] === 1;
  }

  /**
   * Clearance, in cells, that a body of the given radius needs around the cell
   * it stands on.
   * @param {number} radius Body radius.
   * @returns {number}
   */
  clearanceCells(radius) {
    return Math.max(0, Math.ceil(0.5 + radius / this.cellSize) - 1);
  }

  /**
   * Whether a body of `radius` fits at a world position without touching solid
   * geometry. Boxes that merely touch at an edge count as clear.
   *
   * @param {number} x World X.
   * @param {number} z World Z.
   * @param {number} [radius] Body radius.
   * @returns {boolean}
   */
  isClear(x, z, radius = 0) {
    const cs = this.cellSize;
    const x0 = Math.floor((x - radius - this.originX) / cs);
    const x1 = Math.ceil((x + radius - this.originX) / cs) - 1;
    const z0 = Math.floor((z - radius - this.originZ) / cs);
    const z1 = Math.ceil((z + radius - this.originZ) / cs) - 1;

    for (let cz = z0; cz <= z1; cz++) {
      for (let cx = x0; cx <= x1; cx++) {
        if (this.isSolidCell(cx, cz)) return false;
      }
    }
    return true;
  }

  /**
   * Grid eroded by `k` cells: 1 means "a body needing k cells of clearance may
   * not stand on the cell". Built lazily and cached, since every agent of the
   * same size shares one.
   *
   * @param {number} k Clearance in cells.
   * @returns {Uint8Array}
   * @private
   */
  _erodedGrid(k) {
    const cached = this._eroded.get(k);
    if (cached) return cached;
    if (k <= 0) {
      this._eroded.set(k, this.solid);
      return this.solid;
    }

    const { width, height } = this;
    // Separable dilation of the solid set: horizontal pass, then vertical.
    const horizontal = new Uint8Array(this.solid.length);
    for (let cz = 0; cz < height; cz++) {
      const row = cz * width;
      for (let cx = 0; cx < width; cx++) {
        let blocked = 0;
        for (let dx = -k; dx <= k && !blocked; dx++) {
          const nx = cx + dx;
          if (nx < 0 || nx >= width || this.solid[row + nx] === 1) blocked = 1;
        }
        horizontal[row + cx] = blocked;
      }
    }

    const result = new Uint8Array(this.solid.length);
    for (let cz = 0; cz < height; cz++) {
      for (let cx = 0; cx < width; cx++) {
        let blocked = 0;
        for (let dz = -k; dz <= k && !blocked; dz++) {
          const nz = cz + dz;
          if (nz < 0 || nz >= height || horizontal[nz * width + cx] === 1) blocked = 1;
        }
        result[cz * width + cx] = blocked;
      }
    }

    this._eroded.set(k, result);
    return result;
  }

  // ---------------------------------------------------------- line of sight

  /**
   * Walks the grid along a segment (Amanatides & Woo) and reports whether it
   * crosses solid geometry. Zero width on purpose: this answers "can the enemy
   * see/shoot the player", not "can it walk there".
   *
   * @param {number} ax Start X.
   * @param {number} az Start Z.
   * @param {number} bx End X.
   * @param {number} bz End Z.
   * @returns {boolean}
   */
  hasLineOfSight(ax, az, bx, bz) {
    let cx = this.cellOfX(ax);
    let cz = this.cellOfZ(az);
    const ex = this.cellOfX(bx);
    const ez = this.cellOfZ(bz);
    if (this.isSolidCell(cx, cz) || this.isSolidCell(ex, ez)) return false;

    const dx = bx - ax;
    const dz = bz - az;
    const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0;
    const stepZ = dz > 0 ? 1 : dz < 0 ? -1 : 0;
    const cs = this.cellSize;

    const tDeltaX = stepX === 0 ? Infinity : Math.abs(cs / dx);
    const tDeltaZ = stepZ === 0 ? Infinity : Math.abs(cs / dz);
    let tMaxX =
      stepX === 0
        ? Infinity
        : (this.originX + (stepX > 0 ? cx + 1 : cx) * cs - ax) / dx;
    let tMaxZ =
      stepZ === 0
        ? Infinity
        : (this.originZ + (stepZ > 0 ? cz + 1 : cz) * cs - az) / dz;

    let steps = 0;
    while ((cx !== ex || cz !== ez) && steps++ < MAX_RAY_STEPS) {
      if (tMaxX < tMaxZ) {
        cx += stepX;
        tMaxX += tDeltaX;
      } else {
        cz += stepZ;
        tMaxZ += tDeltaZ;
      }
      if (this.isSolidCell(cx, cz)) return false;
    }
    return true;
  }

  /**
   * Whether a body of `radius` can travel the segment in a straight line.
   * Sampled rather than swept: at half a cell per sample nothing thinner than
   * the grid itself can slip through.
   *
   * @param {number} ax Start X.
   * @param {number} az Start Z.
   * @param {number} bx End X.
   * @param {number} bz End Z.
   * @param {number} radius Body radius.
   * @returns {boolean}
   */
  isSegmentClear(ax, az, bx, bz, radius) {
    const distance = Math.hypot(bx - ax, bz - az);
    const steps = Math.max(1, Math.ceil(distance / (this.cellSize * 0.5)));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      if (!this.isClear(ax + (bx - ax) * t, az + (bz - az) * t, radius)) return false;
    }
    return true;
  }

  // --------------------------------------------------------------- pathing

  /**
   * A* over the eroded grid, followed by string pulling so the agent walks
   * diagonals instead of stair-stepping along cell centres.
   *
   * @param {number} sx Start X.
   * @param {number} sz Start Z.
   * @param {number} tx Target X.
   * @param {number} tz Target Z.
   * @param {number} radius Body radius.
   * @returns {NavPoint[] | null} Waypoints ending at the target, or null.
   */
  findPath(sx, sz, tx, tz, radius) {
    const k = this.clearanceCells(radius);
    const grid = this._erodedGrid(k);
    const { width, height } = this;

    const startCx = this.cellOfX(sx);
    const startCz = this.cellOfZ(sz);
    const goalCx = this.cellOfX(tx);
    const goalCz = this.cellOfZ(tz);

    if (
      startCx < 0 || startCz < 0 || startCx >= width || startCz >= height ||
      goalCx < 0 || goalCz < 0 || goalCx >= width || goalCz >= height
    ) {
      return null;
    }
    // The goal must be reachable; the start is accepted even when the agent has
    // been shoved into a tight spot, or it could never path its way out.
    if (grid[goalCz * width + goalCx] === 1) return null;

    const start = startCz * width + startCx;
    const goal = goalCz * width + goalCx;
    if (start === goal) return [{ x: tx, z: tz }];

    const stamp = ++this._stamp;
    const heap = this._heap;
    heap.length = 0;

    this._visited[start] = stamp;
    this._closed[start] = 0;
    this._gScore[start] = 0;
    this._fScore[start] = this._heuristic(startCx, startCz, goalCx, goalCz);
    this._cameFrom[start] = -1;
    this._heapPush(start);

    let expansions = 0;
    let found = false;

    while (heap.length > 0 && expansions++ < MAX_EXPANSIONS) {
      const current = this._heapPop();
      if (current === goal) {
        found = true;
        break;
      }
      if (this._closed[current] === 1) continue;
      this._closed[current] = 1;

      const cx = current % width;
      const cz = (current - cx) / width;
      const gCurrent = this._gScore[current];

      for (let dz = -1; dz <= 1; dz++) {
        for (let dx = -1; dx <= 1; dx++) {
          if (dx === 0 && dz === 0) continue;
          const nx = cx + dx;
          const nz = cz + dz;
          if (nx < 0 || nz < 0 || nx >= width || nz >= height) continue;

          const neighbour = nz * width + nx;
          if (grid[neighbour] === 1) continue;
          // No corner cutting: a diagonal needs both orthogonals open.
          if (dx !== 0 && dz !== 0) {
            if (grid[cz * width + nx] === 1 || grid[nz * width + cx] === 1) continue;
          }

          const step = dx !== 0 && dz !== 0 ? Math.SQRT2 : 1;
          const tentative = gCurrent + step;

          if (this._visited[neighbour] !== stamp) {
            this._visited[neighbour] = stamp;
            this._closed[neighbour] = 0;
            this._gScore[neighbour] = Infinity;
          }
          if (tentative >= this._gScore[neighbour]) continue;

          this._cameFrom[neighbour] = current;
          this._gScore[neighbour] = tentative;
          this._fScore[neighbour] = tentative + this._heuristic(nx, nz, goalCx, goalCz);
          this._heapPush(neighbour);
        }
      }
    }

    if (!found) return null;
    return this._reconstruct(goal, start, tx, tz, radius);
  }

  /**
   * Octile distance: the exact cost of an unobstructed 8-connected walk, which
   * keeps the heuristic admissible.
   * @param {number} ax Cell X.
   * @param {number} az Cell Z.
   * @param {number} bx Cell X.
   * @param {number} bz Cell Z.
   * @returns {number}
   * @private
   */
  _heuristic(ax, az, bx, bz) {
    const dx = Math.abs(ax - bx);
    const dz = Math.abs(az - bz);
    return dx + dz + (Math.SQRT2 - 2) * Math.min(dx, dz);
  }

  /**
   * @param {number} goal Goal cell index.
   * @param {number} start Start cell index.
   * @param {number} tx Exact target X.
   * @param {number} tz Exact target Z.
   * @param {number} radius Body radius.
   * @returns {NavPoint[]}
   * @private
   */
  _reconstruct(goal, start, tx, tz, radius) {
    /** @type {NavPoint[]} */
    const raw = [];
    let node = goal;
    let guard = 0;
    while (node !== -1 && node !== start && guard++ < this.solid.length) {
      const cx = node % this.width;
      const cz = (node - cx) / this.width;
      raw.push({ x: this.centerX(cx), z: this.centerZ(cz) });
      node = this._cameFrom[node];
    }
    raw.reverse();
    if (raw.length > 0) raw[raw.length - 1] = { x: tx, z: tz };
    else raw.push({ x: tx, z: tz });

    return this._stringPull(raw, radius);
  }

  /**
   * Drops every waypoint the agent can simply walk past, which removes the
   * stair-stepping an 8-connected grid produces and, with it, most of the
   * oscillation between two equivalent routes.
   *
   * @param {NavPoint[]} points Raw waypoints.
   * @param {number} radius Body radius.
   * @returns {NavPoint[]}
   * @private
   */
  _stringPull(points, radius) {
    if (points.length <= 2) return points;
    /** @type {NavPoint[]} */
    const pulled = [];
    let anchor = 0;

    while (anchor < points.length - 1) {
      let next = anchor + 1;
      for (let candidate = points.length - 1; candidate > anchor + 1; candidate--) {
        const from = points[anchor];
        const to = points[candidate];
        if (this.isSegmentClear(from.x, from.z, to.x, to.z, radius)) {
          next = candidate;
          break;
        }
      }
      pulled.push(points[next]);
      anchor = next;
    }
    return pulled;
  }

  // ------------------------------------------------------------------- heap

  /** @param {number} index Cell index. @returns {void} @private */
  _heapPush(index) {
    const heap = this._heap;
    heap.push(index);
    let child = heap.length - 1;
    while (child > 0) {
      const parent = (child - 1) >> 1;
      if (this._fScore[heap[parent]] <= this._fScore[heap[child]]) break;
      [heap[parent], heap[child]] = [heap[child], heap[parent]];
      child = parent;
    }
  }

  /** @returns {number} Cell index with the lowest f score. @private */
  _heapPop() {
    const heap = this._heap;
    const top = heap[0];
    const last = heap.pop();
    if (heap.length > 0) {
      heap[0] = /** @type {number} */ (last);
      let parent = 0;
      for (;;) {
        const left = parent * 2 + 1;
        const right = left + 1;
        let smallest = parent;
        if (left < heap.length && this._fScore[heap[left]] < this._fScore[heap[smallest]]) {
          smallest = left;
        }
        if (right < heap.length && this._fScore[heap[right]] < this._fScore[heap[smallest]]) {
          smallest = right;
        }
        if (smallest === parent) break;
        [heap[parent], heap[smallest]] = [heap[smallest], heap[parent]];
        parent = smallest;
      }
    }
    return top;
  }
}
